import { CommonModule, isPlatformBrowser, NgOptimizedImage } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  DOCUMENT,
  effect,
  ElementRef,
  inject,
  Injector,
  PLATFORM_ID,
  Renderer2,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { NgxUiLoaderModule, NgxUiLoaderService } from 'ngx-ui-loader';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { InputSwitchModule } from 'primeng/inputswitch';
import { Menu, MenuModule } from 'primeng/menu';
import { OverlayPanel, OverlayPanelModule } from 'primeng/overlaypanel';
import { assets } from '../../../core/constants/assets.const';
import { withLoader } from '../../../core/helpers/loader.operator';
import { TranslatePipe } from '../../../core/pipe/translate.pipe';
import {
  applyDocumentLanguage,
  persistBrowserLanguage,
} from '../../../core/utils/language-storage.util';
import { User } from '../../../models/auth';
import { langugaes } from '../../../models/common';
import { notifications, notificationsResponse } from '../../../models/notification.model';
import { preference } from '../../../models/preference.model';
import { aiService } from '../../../services/ai.service';
import { AuthService } from '../../../services/auth.service';
import { ConfirmDialogService } from '../../../services/confirm-dialog.service';
import { EncryptionService } from '../../../services/encryption.service';
import { I18nService } from '../../../services/i18n.service';
import { InitialService } from '../../../services/initial.service';
import { MalayalamTtsService } from '../../../services/malayalam-tts.service';
import { MessagingService } from '../../../services/messaging.service';
import { NotificationService } from '../../../services/notification.service';
import { preferenceService } from '../../../services/preference.service';
import { SocketsService } from '../../../services/sockets.service';
import { ToggleService } from '../../../services/toggle.service';
import { VoiceAssistantService } from '../../../services/voice-assistant.service';
import { PrefrencesComponent } from '../prefrences/prefrences.component';

/** Payload shape for notification route action */
interface NotificationPayload {
  schedule_type?: string;
  type?: string;
  status?: string;
  enquiry_id?: number;
}

/** Push notification item shape for getInfo */
interface PushNotificationItem {
  payload?: {
    pushType?: number;
    category?: string;
    enquiryId?: number;
  };
}

@Component({
  selector: 'logibids-user-headers',
  templateUrl: './user-headers.component.html',
  styleUrls: ['./user-headers.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslatePipe,
    ConfirmDialogModule,
    OverlayPanelModule,
    InfiniteScrollModule,
    DialogModule,
    InputSwitchModule,
    FormsModule,
    NgOptimizedImage,
    NgxUiLoaderModule,
    PrefrencesComponent,
    RouterModule,
    MenuModule,
  ],
  providers: [ConfirmationService],
})
export class UserHeadersComponent implements AfterViewInit {
  private readonly _authService = inject(AuthService);
  private readonly initialService = inject(InitialService);
  private readonly confirmationService = inject(ConfirmDialogService);
  private readonly _NotificationService = inject(NotificationService);
  private readonly _messagingService = inject(MessagingService);
  private _aiService = inject(aiService);
  private readonly _EncryptionService = inject(EncryptionService);
  private readonly renderer = inject(Renderer2);
  private readonly _InitialService = inject(InitialService);
  private readonly _SocketsService = inject(SocketsService);
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngxLoader = inject(NgxUiLoaderService);
  private readonly toggleService = inject(ToggleService);
  private readonly voiceAssistant = inject(VoiceAssistantService);
  private readonly cdr = inject(ChangeDetectorRef);
  readonly _preferenceService = inject(preferenceService);

  readonly assetsConst = assets;
  readonly dropdown = signal(false);
  readonly language = signal(false);
  readonly privacy = signal(false);
  readonly user = signal<User | null>(null);
  readonly checked = signal(true);
  readonly notifications = signal<notifications[]>([]);
  readonly is_reqto_driver = signal(false);
  readonly pendingDisplay = signal(false);
  readonly rejectedDisplay = signal(false);
  readonly selectedLang = signal('');
  readonly prefrences = signal(false);
  readonly prefrencesData = signal<preference[] | []>([]);
  readonly total = signal(0);
  readonly from = signal(1);
  readonly size = signal(10);

  readonly isOpInitialized = signal(false);
  readonly isOp1Initialized = signal(false);
  readonly isOp2Initialized = signal(false);
  readonly isOp3Initialized = signal(false);

  @ViewChild('op1') overlayPanel1!: OverlayPanel;
  @ViewChild('op') overlayPanel!: OverlayPanel;
  @ViewChild('op2') overlayPanel2!: OverlayPanel;
  @ViewChild('op3') overlayPanel3!: OverlayPanel;
  @ViewChild('notificationButton', { read: ElementRef })
  notificationButton!: ElementRef;
  @ViewChild('menu') menu!: Menu;

  notification = false;
  is_auto_pay = false;
  private readonly previous_auto_pay = signal(false);

  readonly filteredLanguages = computed<langugaes[]>(() => {
    const langs = this.initialService.languages();
    return langs?.length ? langs : [];
  });

  readonly hasPrefrences = computed(() => {
    const data = this.prefrencesData();
    return Array.isArray(data) && data.length > 0;
  });

  private route = inject(ActivatedRoute);
  private translateService = inject(I18nService);
  private readonly malayalamTts = inject(MalayalamTtsService);
  private router = inject(Router);

  constructor(injector: Injector) {
    if (isPlatformBrowser(this.platformId)) {
      const savedLang = localStorage.getItem('language') || 'en';
      applyDocumentLanguage(savedLang, this.document);
      this.selectedLang.set(savedLang);

      if (this.malayalamTts.shouldUseServerTts(savedLang)) {
        void this.malayalamTts.warmup();
      }
    }

    effect(() => {
      const profile = this._authService.profileDetails();
      if (profile?.token) this.getUser();
      if (profile === null) this.user.set(null);
    });

    effect(() => {
      const data = this._preferenceService.preference();
      const dataHide = this._preferenceService.preferenceHide();
      if (dataHide !== null) this.prefrences.set(dataHide);
      this.prefrencesData.set(data);
      const autoPay = this.toggleService.isOn();
      if (autoPay === false) this.is_auto_pay = false;
    });

    this._messagingService.currentMessage
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        if (!data) return;

        if (data?.action === 'refresh') {
          this.from.set(1);
          this.getNotificationsRefresh();
        } else {
          const result = data?.find((item: any) => item.user_id === this.user()?.id);

          if (result?.id) {
            this.updateNotification(result.id);
          }
        }
      });
  }

  toggleMenu(event: Event): void {
    this.menu.toggle(event);
  }

  toggleLazyOverlay(event: Event, id: 'op' | 'op1' | 'op2' | 'op3', target?: any): void {
    const sigMap = {
      op: this.isOpInitialized,
      op1: this.isOp1Initialized,
      op2: this.isOp2Initialized,
      op3: this.isOp3Initialized,
    };

    // Reset other overlays to ensure they are removed from DOM
    Object.keys(sigMap).forEach(key => {
      if (key !== id) {
        const otherSig = sigMap[key as keyof typeof sigMap];
        if (otherSig()) {
          this.getOverlay(key)?.hide();
          otherSig.set(false);
        }
      }
    });

    const sig = sigMap[id];

    if (!sig()) {
      sig.set(true);
      setTimeout(() => {
        const panel = this.getOverlay(id);
        panel?.show(event, target);
        this.handleOverlayLogic(id);
      }, 0);
    } else {
      const panel = this.getOverlay(id);
      panel?.toggle(event, target);
      this.handleOverlayLogic(id);
    }
  }

  onOverlayHide(id: string): void {
    if (id === 'op') this.isOpInitialized.set(false);
    if (id === 'op1') this.isOp1Initialized.set(false);
    if (id === 'op2') this.isOp2Initialized.set(false);
    if (id === 'op3') this.isOp3Initialized.set(false);
  }

  private getOverlay(id: string): OverlayPanel | undefined {
    if (id === 'op') return this.overlayPanel;
    if (id === 'op1') return this.overlayPanel1;
    if (id === 'op2') return this.overlayPanel2;
    if (id === 'op3') return this.overlayPanel3;
    return undefined;
  }

  private handleOverlayLogic(id: string): void {
    if (id === 'op') this.showNotification();
    if (id === 'op2') this.getLanguages();
  }

  getLanguages(): void {
    this._InitialService.getLanguages();
  }

  ngAfterViewInit(): void {
    if (this._authService.isAuthenticated()) this.getUser();

    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      if (params.get('notification') === 'true') {
        setTimeout(() => {
          this.openOverlay();
          this.removeQueryParam();
        }, 0);
      }
    });
  }

  removeQueryParam(): void {
    this.router.navigate([], {
      queryParams: { notification: null },
      queryParamsHandling: 'merge',
    });
  }

  openOverlay(): void {
    setTimeout(() => {
      if (this.overlayPanel && this.notificationButton) {
        const event = new MouseEvent('click', { bubbles: true });
        this.overlayPanel.show(event, this.notificationButton.nativeElement);
      }
    }, 100);
  }

  showNotification(): void {
    this.from.set(1);
    this.getNotifications();
  }

  getNotifications(): void {
    const query = `from=${this.from()}&size=${this.size()}`;
    this._NotificationService
      .getNotifications(query)
      .pipe(withLoader(this.ngxLoader, 'notificationLoader'), takeUntilDestroyed(this.destroyRef))
      .subscribe((res: notificationsResponse) => {
        this.notifications.set([...this.notifications(), ...res.list]);
        this.total.set(res.total);
        this.from.set(this.from() + 1);
      });
  }

  getNotificationsRefresh(): void {
    const query = `from=${this.from()}&size=${this.size()}`;
    this._NotificationService
      .getNotificationsRefresh(query)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res: notificationsResponse) => {
        const newItems = res.list;
        this.notifications.set([]);
        this.notifications.set([...this.notifications(), ...newItems]);
        this.total.set(res.total);
        this.from.set(this.from() + 1);
      });
  }

  updateNotification(notifiId: number): void {
    this._NotificationService
      .updateNotificationById(notifiId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.from.set(1);
        this.notifications.set([]);
        this.getNotifications();
        this.getUser();
      });
  }

  onScrollingFinished(): void {
    if (this.notifications().length >= this.total()) return;
    this.getNotifications();
  }

  btn_routeNotification(
    type: string,
    id: number,
    notifiId: number,
    payload: NotificationPayload,
  ): void {
    console.log('btn_routeNotification', type, id, notifiId, payload);
    this.hideOP();
    const baseUrl =
      payload.schedule_type === 'IMMEDIATE'
        ? '/delivery/details/'
        : '/schedule-rent-transport/details/';
    const routes: Record<string, string> = {
      ENQUIRY: baseUrl + id,
      TRIP: `/tracking/${id}/${payload.schedule_type === 'IMMEDIATE' ? 3 : 2}`,
      TRANSACTION: '/wallet',
      REFERRAL: '/refer-earn/refer-earn-home',
      REFERRAL_CREDIT: '/refer-earn/refer-histroy?tab=1',
      REFERRAL_PAYOUT: '/refer-earn/refer-histroy?tab=2',
      REFERRAL_STATUS: '/refer-earn/refer-activity',
    };
    const url = routes[type];
    if (url) {
      this.router
        .navigateByUrl('/services', { skipLocationChange: true })
        .then(() => this.router.navigateByUrl(url));
    }
    this.updateNotification(notifiId);
  }

  hideOP(): void {
    this.overlayPanel1?.hide();
    this.overlayPanel2?.hide();
    this.overlayPanel3?.hide();
    this.overlayPanel?.hide();
  }

  getUser(): void {
    this._authService.userProfile().subscribe((user: User) => {
      this.user.set(user);
      const current = this.user();
      if (!current) return;

      const pending = (
        current as User & {
          pending_payment?: {
            is_payment_pending?: boolean;
            enquiry_id?: number;
          };
        }
      ).pending_payment;
      if (pending?.is_payment_pending) {
        this.router.navigateByUrl('/transport/extra-charges/' + pending.enquiry_id);
        return;
      }

      const enquiry = (current as User & { enquiry?: { status: string; enquiry_id: number } })
        .enquiry;
      if (enquiry) {
        const url = this.router.url;
        if (!url.startsWith('/transport/cabs')) {
          if (enquiry.status === 'RUNNING') {
            this.router.navigateByUrl('/transport/cabs?enquiryId=' + enquiry.enquiry_id);
          } else if (enquiry.status === 'ACCEPTED') {
            this.router.navigateByUrl('/transport/review/' + enquiry.enquiry_id);
          }
        }
      }

      this._EncryptionService.encryptLocalStorage(user, 'profile');

      if (isPlatformBrowser(this.platformId)) {
        if (user.iscontract === false) {
          this.clearUserSessionAndNavigate();
          return;
        }
        this.checked.set(user.active);
        this.is_reqto_driver.set(user.is_reqto_driver);
        this.notification = user.notification;
        this.is_auto_pay = user.is_auto_pay;
      }
    });
  }

  toggleSignal(sig: WritableSignal<boolean>): void {
    sig.set(!sig());
    this.hideOP();
  }

  showNav(): void {
    this.toggleSignal(this.dropdown);
    this.language.set(false);
    this.privacy.set(false);
  }

  showLang(): void {
    this.toggleSignal(this.language);
    this.dropdown.set(false);
    this.privacy.set(false);
  }

  showPrivacy(): void {
    this.toggleSignal(this.privacy);
    this.language.set(false);
    this.dropdown.set(false);
  }

  gotoPage(path: string, queryParams: Record<string, unknown> = {}): void {
    this.router.navigate([path], { queryParams });
    this.hideOP();
  }

  gotoPrivacy(): void {
    this.gotoPage('/public/web/PRIVACY_POLICY');
  }
  gotoAboutUs(): void {
    this.gotoPage('/public/web/ABOUT_US');
  }
  gotoTerms(): void {
    this.gotoPage('/public/web/TERMS_CONDITIONS');
  }
  gotoFaq(): void {
    this.gotoPage('/public/faq');
  }
  gotoContact(): void {
    this.gotoPage('/public/contact');
  }
  gotoMyProfile(): void {
    this.gotoPage('/my-profile');
  }
  gotoMyPreferences(): void {
    this.gotoPage('/sp-profile/profile-docs');
  }
  gotoMyDrivers(): void {
    this.gotoPage('/sp/my-drivers');
  }
  gotoMyVehicles(): void {
    this.gotoPage('/sp/my-vehicles');
  }
  goToMyWallet(): void {
    this.gotoPage('/wallet');
  }
  goToLoyality(): void {
    this.gotoPage('/loyality-points');
  }
  goToRefer(): void {
    this.gotoPage('/refer-earn/refer-earn-home');
  }
  gotoMyTrips(): void {
    this.gotoPage('/my-trips');
  }
  gotoHome(): void {
    this.gotoPage('/services');
  }
  gotoMyRating(): void {
    this.gotoPage('/sp/my-rating');
  }
  deleteUser(): void {
    this.hideOP();
    this.router.navigateByUrl('/account-control');
  }

  gotoChangePassword(): void {
    this.gotoPage('/my-profile/change-password', {
      phoneNumber: this.user()?.phone_number,
    });
  }

  private clearUserSessionAndNavigate(): void {
    this.voiceAssistant.disarm();
    this.user.set(null);
    this._authService.profileDetails.set(null);
    this.from.set(1);
    this.notifications.set([]);
    if (isPlatformBrowser(this.platformId)) {
      const currLang = localStorage.getItem('language');
      localStorage.clear();
      localStorage.setItem('language', currLang || '');
    }
    this.router.navigateByUrl('/services', { skipLocationChange: true }).then(() => {
      this._SocketsService.disconnect();
      this.router.navigateByUrl('/auth/login');
    });
  }

  btn_logOut(): void {
    this.confirmationService
      .showConfirmationDialog(' ', this.translateService.translate('SureLogout'))
      .then(result => {
        if (result && isPlatformBrowser(this.platformId)) {
          this._authService.logout().subscribe(() => {
            this.hideOP();
            this.clearUserSessionAndNavigate();
          });
        }
      });
  }

  setLanguage(language: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    void this.applyLanguageChange(language);
  }

  private async applyLanguageChange(language: string): Promise<void> {
    const previousLang = localStorage.getItem('language') || 'en';

    if (
      this.malayalamTts.isMalayalamLanguage(previousLang) &&
      !this.malayalamTts.isMalayalamLanguage(language)
    ) {
      this.malayalamTts.dispose();
    }

    this.selectedLang.set(language);
    persistBrowserLanguage(language);

    try {
      await this.translateService.ensureLoaded(language);
    } catch {
      // Translations are reloaded on softReload; continue so cookie/profile still update.
    }

    applyDocumentLanguage(language, this.document);
    this.initialService.refreshGoogleMaps(language);
    this.hideOP();
    this.changeLanguagesUser(language);

    if (this.malayalamTts.shouldUseServerTts(language)) {
      void this.malayalamTts.warmup();
    }
  }

  changeLanguagesUser(language: string): void {
    const payload = { preferred_language: language } as User;
    this._authService.updateProfile(payload).subscribe({
      next: () => {
        if (isPlatformBrowser(this.platformId)) {
          this.softReload();
        }
      },
      error: () => {
        if (isPlatformBrowser(this.platformId)) {
          this.softReload();
        }
      },
    });
  }

  private softReload() {
    window.location.reload();
  }

  getFormattedMessage(message: string): string {
    if (this.translateService.getCurrentLang() !== 'ar') {
      return message;
    }

    // Unicode LTR isolate markers
    const LTR_ISOLATE = '\u2066';
    const POP_ISOLATE = '\u2069';

    // Match tokens like #IN23898 — hash followed by alphanumerics
    return message.replace(/(#[A-Za-z0-9]+)/g, `${LTR_ISOLATE}$1${POP_ISOLATE}`);
  }

  updateUserField(field: keyof User, value: unknown, oldValue?: boolean): void {
    const payload = { [field]: value } as unknown as User;
    this._authService
      .updateProfile(payload)
      .pipe(withLoader(this.ngxLoader, 'uniqueLoader2'))
      .subscribe({
        next: (resp: User) => {
          const current = this.user();
          if (current) this.user.set({ ...current, [field]: value });
          this._EncryptionService.encryptLocalStorage(resp, 'profile');
          if (
            field === 'is_auto_pay' &&
            this.router.url.includes('/schedule-rent-transport/details/')
          ) {
            this.reloadCurrentRoute();
          }
        },
        error: () => {
          this.is_auto_pay = oldValue ?? this.is_auto_pay;
          this.cdr.detectChanges();
        },
      });
  }

  notificationOnOff(event: { checked: boolean }): void {
    this.updateUserField('notification', event.checked);
  }

  autoPaymentOnOff(event: { checked: boolean }): void {
    const newValue = event.checked;
    this.previous_auto_pay.set(!newValue);
    this.updateUserField('is_auto_pay', newValue, !newValue);
  }

  private reloadCurrentRoute(): void {
    const url = this.router.url;
    const strategy = this.router.routeReuseStrategy;
    const prevShouldReuseRoute = strategy.shouldReuseRoute;
    const prevOnSameUrlNavigation = this.router.onSameUrlNavigation;

    strategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = 'reload';

    this.router.navigateByUrl(url).finally(() => {
      strategy.shouldReuseRoute = prevShouldReuseRoute;
      this.router.onSameUrlNavigation = prevOnSameUrlNavigation;
    });
  }

  getInfo(item: PushNotificationItem): void {
    if (!isPlatformBrowser(this.platformId) || !item?.payload) return;
    const { category: cat, enquiryId: enqId, pushType } = item.payload;
    const language = localStorage.getItem('language') || 'en';
    const usr = this.user();

    if (pushType === 8) {
      this.router
        .navigate(['/sp/go-trips'], {
          queryParams: { type: cat, id: enqId },
        })
        .then(() =>
          this.router.navigateByUrl('/', { skipLocationChange: true }).then(() =>
            this.router.navigate(['/sp/go-trips'], {
              queryParams: { type: cat, id: enqId },
            }),
          ),
        );
      return;
    }

    if (usr?.type !== 'SERVICE_PROVIDER') {
      this.router.navigate(['/sp/my-trips']);
      return;
    }

    if (cat === 'DELIVERY') {
      this.router
        .navigate(['/sp-bids/sp-new-delivery'], {
          queryParams: { lang: language, id: enqId, type: cat },
        })
        .then(() =>
          this.router.navigateByUrl('/', { skipLocationChange: true }).then(() =>
            this.router.navigate(['/sp-bids/sp-new-delivery'], {
              queryParams: { lang: language, id: enqId, type: cat },
            }),
          ),
        );
    } else {
      this.router
        .navigate(['/sp-bids/sp-new-bid'], {
          queryParams: { id: enqId, type: cat, lang: language },
        })
        .then(() =>
          this.router.navigateByUrl('/', { skipLocationChange: true }).then(() =>
            this.router.navigate(['/sp-bids/sp-new-bid'], {
              queryParams: { id: enqId, type: cat, lang: language },
            }),
          ),
        );
    }
  }

  /** Template calls hidePrefernces($event); param kept for API compatibility */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  hidePrefernces(_event?: boolean): void {
    this.prefrences.set(!this.prefrences());
  }

  goToHelpGuide() {
    this.router.navigate(['/public/helpGuide']);
    this.hideOP();
  }

  isTripEndedNotification(list: notifications): boolean {
    const payload = list.payload as { status?: string };
    return list.message_type === 'TRIP_ENDED_NOTIFICATION' && payload?.status === 'ENDED';
  }

  openAI(): void {
    this._aiService.setData(true);
    this.hideOP();
  }
}
